import {
  LOAD_TARIFICATION_SETTINGS,
  LOGIN_EMR_NO_LICENSE,
  LOGIN_EMR_SERVER,
  LOGIN_EMR_SERVER_ERROR,
  LOGIN_EMR_SERVER_FETCHING,
  LOGIN_EMR_SERVER_SUCCESS,
  LOGIN_EMR_SERVER_TENANT_OPTIONS,
  LOGIN_PASSPORT_CALLBACK,
  LOGIN_STS_RECEIVED,
  LOGOUT_UNAUTHORIZED,
  SET_USER_CONFIGURATION,
  UserContextActionTypes,
} from "store/types/user-context.types";
import {
  createStsSession,
  fetchTenants,
  getEHealthPassword,
  loginWithToken,
} from "api/login";
import { Dispatch } from "redux";
import { TenantOption } from "common/dto/TenantOption";
import {
  createHelenaSession,
  isHelenaEnabledInConfig,
} from "store/actions/helena.actions";
import { HelenaAction } from "store/types/helena.types";
import { Config } from "config";
import { createTarificationApplicationSession } from "store/actions/tarification.actions";
import { TarificationAction } from "store/types/tarification.types";
import { AxiosError } from "axios";
import { i18nUserContext } from "config/translations/I18n";
import { loadTenantData } from "./tenant-context.actions";
import { TenantContextAction } from "../types/tenant-context.types";
import { newEHealthPassword } from "store/actions/session.actions";
import { SessionAction } from "store/types/session.types";
import { ThunkAction } from "redux-thunk";
import { GlobalState } from "store/configure-store";
import { UserConfiguration } from "model/user-configuration.model";

/** ---- ACTIONS ---- */
export const getCorilusPassportURL = (visitConfig: Config): string => {
  const getUrl = window.location;
  const baseUrl = getUrl.protocol + "//" + getUrl.host;

  const searchParams = new URLSearchParams();
  searchParams.append("response_type", "id_token");
  searchParams.append("response_mode", "fragment");
  searchParams.append("client_id", "cc_visit");
  searchParams.append(
    "redirect_uri",
    baseUrl + "/auth/login/passport/callback"
  );
  searchParams.append("scope", "openid");
  searchParams.append("brand", "corilus");
  searchParams.append("acr_values", "LOA_3");
  searchParams.append("state", btoa(JSON.stringify({})));
  searchParams.append(
    "nonce",
    Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
  );

  return (
    visitConfig.corilusPassportUrl + "/authorize?" + searchParams.toString()
  );
};

export const passportCallback = (
  idToken: string,
  state: any
): ThunkAction<
  Promise<void>,
  GlobalState,
  any,
  | UserContextActionTypes
  | HelenaAction
  | TarificationAction
  | TenantContextAction
  | SessionAction
> => {
  return async (dispatch) => {
    dispatchCallbackPassport(idToken, state)(dispatch);
    dispatchTenantsFetching()(dispatch);
    let tenantOptions: TenantOption[] = [];
    try {
      tenantOptions = await fetchTenants(idToken);
    } catch (e) {
      dispatchLoginError(e.toString())(dispatch);
      return;
    }
    switch (tenantOptions.length) {
      case 0:
        dispatchLoginError(i18nUserContext.t("error.noTenants"))(dispatch);
        break;
      case 1:
        await dispatch(loginEMRServer(idToken, tenantOptions[0].name));
        break;
      default:
        tenantOptions.sort((a, b) => a.name.localeCompare(b.name));
        dispatchTenantOptions(tenantOptions)(dispatch);
        break;
    }
  };
};

export const loginEMRServer = (
  idToken: string,
  tenant: string
): ThunkAction<
  Promise<void>,
  GlobalState,
  any,
  | UserContextActionTypes
  | HelenaAction
  | TarificationAction
  | TenantContextAction
  | SessionAction
> => {
  return async (dispatch, getState) => {
    try {
      dispatchLoginStart(idToken)(dispatch);
      const response = await loginWithToken(idToken, tenant);
      dispatchLoginSuccess(response)(dispatch);

      if (isHelenaEnabledInConfig(response.userConfiguration)(dispatch)) {
        await dispatch(createHelenaSession(response.user.id));
      }
      loadTenantData()(dispatch);
      const eHealthPassword = await getEHealthPassword(
        idToken,
        response.user.corilusPersonId,
        response.userConfiguration.ehealthKeyStorePasswordKey,
        tenant
      );
      const stsToken = await createStsSession(
        response.user.id,
        eHealthPassword
      );
      newEHealthPassword(eHealthPassword)(dispatch);
      dispatchStsTokenReceived(stsToken)(dispatch);
      createTarificationApplicationSession(
        response.user.id,
        stsToken,
        eHealthPassword
      )(dispatch);
      return Promise.resolve();
    } catch (e) {
      handleLoginException(e, dispatch);
      return Promise.reject(e);
    }
  };
};

const handleLoginException = (
  e: AxiosError,
  dispatch: Dispatch<UserContextActionTypes>
) => {
  if (e.response && e.response.status === 441) {
    dispatchNoLicense(
      e.response.data.message,
      e.response.data.description
    )(dispatch);
  } else {
    dispatchLoginError(e.response && e.response.data.message)(dispatch);
  }
};

/** ---- DISPATCHERS ---- */
const dispatchStsTokenReceived = (stsToken: string) => {
  return (dispatch: Dispatch<UserContextActionTypes>) => {
    dispatch({ type: LOGIN_STS_RECEIVED, payload: stsToken });
  };
};

const dispatchTenantsFetching = () => {
  return (dispatch: Dispatch<UserContextActionTypes>) => {
    dispatch({ type: LOGIN_EMR_SERVER_FETCHING });
  };
};

export const dispatchLogout = () => {
  return (dispatch: Dispatch<UserContextActionTypes>) => {
    dispatch({ type: LOGOUT_UNAUTHORIZED });
  };
};

const dispatchCallbackPassport = (idToken: string, state: string) => {
  return (dispatch: Dispatch<UserContextActionTypes>) => {
    dispatch({
      type: LOGIN_PASSPORT_CALLBACK,
      payload: { idToken: idToken, state: state },
    });
  };
};

export const dispatchTenantOptions = (tenantOptions: Array<TenantOption>) => {
  return (dispatch: Dispatch<UserContextActionTypes>) => {
    dispatch({
      type: LOGIN_EMR_SERVER_TENANT_OPTIONS,
      payload: tenantOptions,
    });
  };
};

export const dispatchLoginError = (errorMessage: string) => {
  return (dispatch: Dispatch<UserContextActionTypes>) => {
    dispatch({
      type: LOGIN_EMR_SERVER_ERROR,
      payload: errorMessage,
    });
  };
};

export const dispatchNoLicense = (errorMessage: string, licenseUrl: string) => {
  return (dispatch: Dispatch<UserContextActionTypes>) => {
    dispatch({
      type: LOGIN_EMR_NO_LICENSE,
      payload: {
        loginError: errorMessage,
        licenseUrl: licenseUrl,
      },
    });
  };
};

export const dispatchTarificationUserSettingsLoaded = (useEAttest: boolean) => {
  return (dispatch: Dispatch<UserContextActionTypes>) => {
    dispatch({
      type: LOAD_TARIFICATION_SETTINGS,
      payload: useEAttest,
    });
  };
};

export const dispatchSetUserConfiguration = (
  userConfiguration: UserConfiguration
) => {
  return (dispatch: Dispatch<UserContextActionTypes>) => {
    dispatch({
      type: SET_USER_CONFIGURATION,
      payload: userConfiguration,
    });
  };
};

const dispatchLoginStart = (idToken: string) => {
  return (dispatch: Dispatch<UserContextActionTypes>) => {
    dispatch({ type: LOGIN_EMR_SERVER, payload: idToken });
  };
};

const dispatchLoginSuccess = (response: any) => {
  return (dispatch: Dispatch<UserContextActionTypes>) => {
    dispatch({
      type: LOGIN_EMR_SERVER_SUCCESS,
      payload: response,
    });
  };
};
